import { Button as MuiButton, type ButtonProps as MuiButtonProps, CircularProgress } from '@mui/material';
import { forwardRef } from 'react';

import { calculateIconSize } from './calculateIconSize';
import { getVariantStyles } from './getVariantStyles';

type ButtonVariants = 'primary' | 'secondary' | 'text';

type ButtonOptions = {
  loading?: boolean;
  variant?: ButtonVariants;
};

export type ButtonProps<C extends React.ElementType> = Omit<
  MuiButtonProps<
    C,
    {
      component?: C;
    }
  >,
  'color' | 'variant'
> &
  ButtonOptions;

const Component = <C extends React.ElementType = 'button'>(
  props: ButtonProps<C>,
  ref: React.ForwardedRef<HTMLButtonElement>
) => {
  const { loading, variant, size, endIcon, disabled, ...rest } = props;
  const { variant: variantStyle, color } = getVariantStyles(variant);

  return (
    <MuiButton
      {...rest}
      ref={ref}
      size={size}
      variant={variantStyle}
      color={color}
      disabled={loading || disabled}
      endIcon={loading ? <CircularProgress size={calculateIconSize(size)} /> : endIcon}
    />
  );
};

export const Button = forwardRef(Component) as <C extends React.ElementType = 'button'>(
  props: ButtonProps<C>
) => JSX.Element;
